import React from 'react';
import { Box, IconButton, keyframes, Stack, SxProps, Theme } from '@mui/material';
import { theme } from '../../../../../../common/styles/theme';
import { Text } from '../../../../../../common/components/Text/Text';
import { Paper } from '../../../../../../common/components/Paper/Paper';
import { SpeakerIcon } from '../../../../../../../icons/SpeakerIcon';
import { CheckIcon } from '../../../../../../../icons/CheckIcon';
import { CloseIcon } from '../../../../../../../icons/CloseIcon';
import { textSpeaker } from '../../../../../effects/textSpeaker';
import { TimestampButton } from '../../TimestampButton/TimestampButton';
import { NativeTooltip } from '../../NativeTooltip/NativeTooltip';
import { CLASS_PLAY_CONTROL } from '../../Audio/components/constants';
import { IRestExerciseAnswer, TExerciseChoiceItem } from '../../../../../../common/rest/exercises/exercisesRest';
import { splitChoiceDiff } from './choiceDiff';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

interface IExerciseChoiceProps {
	items: TExerciseChoiceItem[];
	answers: Record<number, IRestExerciseAnswer>;
	langCode?: string;
	// viewing another participant's answers — nothing here may be picked
	readOnly?: boolean;
	// only for "Correct the mistake", where the choices are versions of one phrase
	highlightDiff?: boolean;
	// questions and explanations in the viewer's native language, index-aligned with items
	native?: { question: string, explanation: string }[];
	onAnswer: (questionIndex: number, choice: number) => void;
	onTimestampClick: (time: number) => void;
}

const REVEAL_DELAY_MS = 900;

const shake = keyframes`
	0%, 100% { transform: translateX(0); }
	20%, 60% { transform: translateX(-4px); }
	40%, 80% { transform: translateX(4px); }
`;

const fadeIn = keyframes`
	from { opacity: 0; }
	to { opacity: 1; }
`;

const changedWordSx = {
	fontWeight: 600,
	textDecoration: 'underline',
	textDecorationColor: theme.palette.primary.main,
	textUnderlineOffset: '3px',
};

// line height of the question row — the number, speaker button and timestamp all match it
const QUESTION_LINE = theme.spacing(2.5);

const SpeakButton: React.FC<{ text: string, langCode?: string, sx?: SxProps<Theme> }> = ({ text, langCode, sx }) => (
	<IconButton
		size={'small'}
		// stopPropagation: inside an answer row, a click here must not also pick that answer
		onClick={(e) => { e.stopPropagation(); textSpeaker.speak(text, langCode); }}
		sx={{ color: theme.palette.grey[400], flexShrink: 0, ...(sx as object) }}
	>
		<SpeakerIcon sx={{ width: theme.spacing(2), height: theme.spacing(2) }} />
	</IconButton>
);

export const ExerciseChoice: React.FC<IExerciseChoiceProps> = ({
	items,
	answers,
	langCode,
	readOnly,
	highlightDiff,
	native,
	onAnswer,
	onTimestampClick,
}) => {

	// questions just answered wrong whose right answer isn't revealed yet — the red pick shows first,
	// so the user registers "that was wrong" before seeing what was right
	const [revealPending, setRevealPending] = React.useState<Set<number>>(new Set());
	const timersRef = React.useRef<Array<ReturnType<typeof setTimeout>>>([]);
	React.useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

	const pick = (questionIndex: number, choiceIndex: number, correctIndex: number) => {
		if (choiceIndex !== correctIndex) {
			setRevealPending(prev => new Set(prev).add(questionIndex));
			timersRef.current.push(setTimeout(() => {
				setRevealPending(prev => { const next = new Set(prev); next.delete(questionIndex); return next; });
			}, REVEAL_DELAY_MS));
		}
		onAnswer(questionIndex, choiceIndex);
	};

	return (
	<Stack gap={1.5}>
		{items.map((item, questionIndex) => {
			const answer = answers[questionIndex];
			const picked = answer ? (answer.answer as { choice: number }).choice : undefined;
			const revealed = picked !== undefined && !revealPending.has(questionIndex);
			const diff = highlightDiff ? splitChoiceDiff(item.choices) : null;
			return (
				<Paper
					key={questionIndex}
					sx={{
						p: theme.spacing(1.5),
						background: theme.palette.grey[50],
					}}
				>
					{/* grid, so the number sits on the question's first line even when a context block comes first */}
					<Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 1 }}>
							{item.context &&
							<Box
								// the lesson sentence the timestamp points at — a click plays it, same as the time badge
								className={item.timestamp !== undefined ? CLASS_PLAY_CONTROL : undefined}
								onClick={() => item.timestamp !== undefined && onTimestampClick(item.timestamp)}
								sx={{
									gridColumn: 2,
									p: theme.spacing(1),
									fontSize: theme.typography.subtitle2,
									fontStyle: 'italic',
									borderRadius: theme.spacing(0.5),
									background: theme.palette.common.white,
									transition: 'background .3s ease',
									...(item.timestamp !== undefined && {
										cursor: 'pointer',
										'&:hover': { background: theme.palette.grey[100] },
									}),
								}}
							>
								{item.context}
							</Box>
							}

							<Text sx={{ gridColumn: 1, fontWeight: 600, color: theme.palette.grey[400], lineHeight: QUESTION_LINE }}>
								{questionIndex + 1}.
							</Text>
							<Stack direction={'row'} alignItems={'flex-start'} gap={.5} sx={{ gridColumn: 2, minWidth: 0 }}>
								<SpeakButton text={item.question} langCode={langCode} sx={{ height: QUESTION_LINE, width: QUESTION_LINE }} />
								<Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
									<NativeTooltip text={native?.[questionIndex]?.question}>
										<Text
											component={'span'}
											sx={{
												fontSize: theme.typography.subtitle2,
												fontWeight: 600,
												lineHeight: QUESTION_LINE,
											}}
										>
											{item.question}
										</Text>
									</NativeTooltip>
								</Box>
								{item.timestamp !== undefined &&
									<TimestampButton time={item.timestamp} onClick={onTimestampClick} sx={{ mt: -.25 }} />
								}
							</Stack>

						<Stack sx={{ gridColumn: 2, minWidth: 0 }} gap={1}>

							<Stack gap={.75}
								sx={{
								}}
							>
								{item.choices.map((choice, choiceIndex) => {
									const isCorrect = choiceIndex === item.answer;
									const isPicked = choiceIndex === picked;
									const showResult = picked !== undefined;
									const showCorrect = revealed && isCorrect;
									return (
										<Stack key={choiceIndex} direction={'row'} alignItems={'flex-start'} gap={.5}>
										{/* outside the answer box, so it isn't part of the clickable answer; mt matches the box's padding */}
										<SpeakButton text={choice} langCode={langCode} sx={{ height: QUESTION_LINE, width: QUESTION_LINE, mt: .75 }} />
										<Stack
											direction={'row'}
											alignItems={'flex-start'}
											gap={1}
											onClick={() => !showResult && !readOnly && pick(questionIndex, choiceIndex, item.answer)}
											sx={{
												flex: '1 1 auto',
												minWidth: 0,
												p: theme.spacing(.75, 1),
												borderRadius: theme.spacing(0.5),
												cursor: showResult || readOnly ? 'default' : 'pointer',
												background: showCorrect
													? theme.palette.success.main + '22'
													: showResult && isPicked
														? theme.palette.error.main + '22'
														: theme.palette.common.white,
												transition: 'background .4s ease',
												animation: showResult && isPicked && !isCorrect && revealPending.has(questionIndex) ? `${shake} .4s ease` : 'none',
												'&:hover': showResult || readOnly ? {} : { background: theme.palette.grey[100] },
											}}
										>
											<Box
												sx={{
													width: theme.spacing(2.5),
													height: QUESTION_LINE,
													borderRadius: '50%',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													fontSize: theme.typography.subtitle2,
													fontWeight: 600,
													flexShrink: 0,
													// background: theme.palette.grey[100],
													color: theme.palette.grey[700],
												}}
											>
												{LETTERS[choiceIndex] || choiceIndex + 1}
											</Box>
											<Text
												sx={{
													flex: '1 1 auto',
													fontSize: theme.typography.subtitle1,
													lineHeight: QUESTION_LINE,
												}}
											>
												{diff
													? diff[choiceIndex].map((part, i) => part.changed
														? <Box key={i} component={'span'} sx={changedWordSx}>{part.text}</Box>
														: part.text)
													: choice}
											</Text>
											{showCorrect && <CheckIcon sx={{ color: theme.palette.success.main }} />}
											{showResult && isPicked && !isCorrect && <CloseIcon sx={{ color: theme.palette.error.main }} />}
										</Stack>
										</Stack>
									);
								})}
							</Stack>

							{revealed &&
							<Stack gap={.25} sx={{ animation: `${fadeIn} .4s ease` }}>
								<Text
									sx={{
										color: picked === item.answer ? theme.palette.success.main : theme.palette.error.main,
										fontSize: theme.typography.subtitle2,
										fontWeight: 600,
									}}
								>
									{picked === item.answer ? 'Correct!' : 'Not quite.'}
								</Text>
								{item.explanation &&
								<Box>
									<NativeTooltip text={native?.[questionIndex]?.explanation}>
										<Text
											component={'span'}
											sx={{
												color: theme.palette.grey[500],
												fontSize: theme.typography.subtitle2,
											}}
										>{item.explanation}</Text>
									</NativeTooltip>
								</Box>
								}
								{/* with a context (gap fill, error correction) the quote is that same line again */}
								{item.quote && !item.context &&
								<Box
									component={'button'}
									// plays the phrase: the player's document click handler must not pause it right away
									className={item.timestamp !== undefined ? CLASS_PLAY_CONTROL : undefined}
									onClick={() => item.timestamp !== undefined && onTimestampClick(item.timestamp)}
									sx={{
										textAlign: 'left',
										border: 'none',
										background: 'none',
										p: 0,
										cursor: item.timestamp !== undefined ? 'pointer' : 'default',
										color: theme.palette.grey[900],
										fontSize: theme.typography.subtitle2,
									}}
								>
									“{item.quote}”
								</Box>
								}
							</Stack>
							}
						</Stack>
					</Box>
				</Paper>
			);
		})}
	</Stack>
	);
};
